import { Tab, Tabs } from '@nextui-org/react-latest'
import styled from '@emotion/styled'
import Image from 'next/image'
import { fontFamilies } from '@configs/styles/config'
import { useTranslation } from 'react-i18next'
import { useRef, useState, useEffect } from 'react'
import { motion, useScroll } from 'framer-motion'
import useWindowDimensions from '@hooks/useWindowDimensions'

const Container = styled.div`
  min-height: 100dvh;
  padding: 5rem 1rem 2rem 1rem;
  position: relative;
  max-height: 100%;
  /* Clip the rounded corners so tab images never spill past the card edges
     (notably on tablets, where the stacked layout shows full-width images). */
  overflow: hidden;
`

const ContentWrapper = styled.div`
  max-width: 1380px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`

const Header = styled.div`
  text-align: center;
  @media (min-width: 768px) {
    margin-bottom: 3rem;
  }
`

const MainTitle = styled.h2`
  font-size: 3rem;
  font-weight: bold;
  margin-bottom: 2rem;

  background: linear-gradient(90deg, #727699 0%, #bec5ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (min-width: 768px) {
    font-size: 4rem;
  }
`

const TabContent = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
  max-width: 1152px;
  margin: 3rem auto 0 auto;
  min-height: 440px;
  overflow-y: auto;
  max-height: 100%;
  @media (max-width: 1023px) {
    display: flex;
    flex-direction: column-reverse;
    gap: 2rem;
  }
`

const TextContent = styled.div`
  color: white;
`

const TabTitle = styled.h3`
  font-size: 2rem;
  font-family: ${fontFamilies.bold};
  margin-bottom: 1.5rem;
  color: white;
`

const TabDescription = styled.p`
  font-size: 18px;
  line-height: 1.6;
  font-family: ${fontFamilies.regular};
  color: rgba(255, 255, 255, 0.8);
  @media (min-width: 640px) {
    font-size: 20px;
  }
`

const VisualizationArea = styled.div`
  border-radius: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  /* Allow this grid/flex item to shrink below the image's intrinsic width so
     the image scales down instead of overflowing the card. */
  min-width: 0;
  width: 100%;
  overflow: hidden;
  @media (min-width: 640px) {
    min-height: 300px;
  }
`
export interface Tab {
  label: string
  title: string
  description: string
  image: string
  alt: string
}

// Below this width (tablets and phones) we render the simple stacked layout
// instead of the desktop scroll-jacked tabs, which break on touch devices.
const DESKTOP_BREAKPOINT = 1024

const MustExistSection = ({
  initialTabs,
  title,
  containerClassname,
}: {
  initialTabs?: Tab[]
  title?: string
  containerClassname?: string
}) => {
  const { t } = useTranslation('homeLanding/mustexist')
  const [selected, setSelected] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const tabsRef = useRef<HTMLDivElement>(null)
  const { width } = useWindowDimensions()

  // Scroll-linked progress for each tab
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  })

  const tabs = initialTabs || [
    {
      label: t('tabs.personalizedLearning.label'),
      title: t('tabs.personalizedLearning.title'),
      description: t('tabs.personalizedLearning.description'),
      image: '/static/images/features/personalized-learning.png',
      alt: t('tabs.personalizedLearning.alt'),
    },
    {
      label: t('tabs.lifetimeEngagement.label'),
      title: t('tabs.lifetimeEngagement.title'),
      description: t('tabs.lifetimeEngagement.description'),
      image: '/static/images/features/lifetime-engagement.png',
      alt: t('tabs.lifetimeEngagement.alt'),
    },
    {
      label: t('tabs.oneStopGrowth.label'),
      title: t('tabs.oneStopGrowth.title'),
      description: t('tabs.oneStopGrowth.description'),
      image: '/static/images/features/one-stop-growth.png',
      alt: t('tabs.oneStopGrowth.alt'),
    },
  ]

  useEffect(() => {
    if (!containerRef.current || width < DESKTOP_BREAKPOINT) return

    const container = containerRef.current
    const containerHeight = container.offsetHeight // cache height
    const viewportHeight = window.innerHeight // static for now

    let ticking = false

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const rectTop = container.getBoundingClientRect().top // only this is dynamic
          const scrolledPast = Math.max(0, -rectTop)
          const totalScrollable = Math.max(1, containerHeight - viewportHeight)
          const scrollProgress = Math.min(scrolledPast / totalScrollable, 1)

          const tabIndex = Math.min(
            Math.floor(scrollProgress * tabs.length),
            tabs.length - 1
          )

          if (tabIndex !== selected) {
            setSelected(tabIndex)
            scrollToTab(tabIndex)
          }

          ticking = false
        })

        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll)

    return () => window.removeEventListener('scroll', handleScroll)
  }, [selected, tabs.length, width])

  const scrollToTab = (index: number) => {
    const button = document.querySelector(`button[data-key="${index}"]`)
    button?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    })
  }

  // Update CSS variable for progress width on scroll
  useEffect(() => {
    if (!tabsRef.current || width < DESKTOP_BREAKPOINT) return

    const updateProgress = (latest: number) => {
      if (!tabsRef.current) return
      const start = selected / tabs.length
      const end = (selected + 1) / tabs.length
      const progressInTab = Math.max(
        0,
        Math.min(1, (latest - start) / (end - start))
      )
      tabsRef.current.style.setProperty('--progress', `${progressInTab * 100}%`)
    }

    const unsubscribe = scrollYProgress.on('change', updateProgress)
    return () => unsubscribe()
  }, [selected, width, tabs.length, scrollYProgress])

  return (
    <div
      ref={containerRef}
      style={{
        height:
          width < DESKTOP_BREAKPOINT ? 'auto' : `${100 * tabs.length}vh`, // Dynamic height: 100vh * number of tabs
        position: 'relative',
      }}
      className="snap-y snap-mandatory"
    >
      <div ref={contentRef} className="lg:h-screen lg:sticky lg:top-0">
        <Container
          className={`bg-uguPurple max-w-full rounded-[30px] ${containerClassname || ''}`}
        >
          <ContentWrapper className="max-w-full">
            <Header className="max-w-full">
              <MainTitle>
                {title || t('title')}

                <div className="absolute scale-[0.6] sm:scale-100 top-[-4rem] lg:top-[2%] lg:right-[2%] right-[0%] z-10">
                  <div className="relative">
                    <svg
                      width="85"
                      height="84"
                      viewBox="0 0 85 84"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="relative z-20"
                    >
                      <path
                        d="M56.2211 -0.00328887L56.899 14.6742C57.4707 27.0555 63.423 38.5691 73.1942 46.1945L84.7776 55.2341L70.1001 55.9119C57.7188 56.4837 46.2052 62.436 38.5798 72.2072L29.5402 83.7906L28.8624 69.1131C28.2906 56.7318 22.3383 45.2181 12.5671 37.5927L0.983758 28.5532L15.6612 27.8754C28.0425 27.3036 39.5562 21.3513 47.1816 11.5801L56.2211 -0.00328887Z"
                        fill="#EFF1FF"
                      />
                    </svg>
                    <div className="bg-[#B368FF] absolute top-0 left-0 w-[110%] sm:w-[120%] h-[110%] sm:h-[120%] blur-[40px] z-10" />
                  </div>
                </div>
              </MainTitle>

              {width >= DESKTOP_BREAKPOINT && (
                <div
                  ref={tabsRef}
                  className="relative inline-block"
                  style={{ '--progress': '0%' } as React.CSSProperties}
                >
                  <style>{`
                    .tabs-with-progress [data-slot="cursor"]::before {
                      content: '';
                      position: absolute;
                      left: 0;
                      top: 0;
                      bottom: 0;
                      width: var(--progress, 0%);
                      background: #c8cef0;
                      z-index: 0;
                    }
                  `}</style>
                  <Tabs
                    className="tabs-with-progress"
                    color="primary"
                    selectedKey={selected.toString()}
                    onSelectionChange={(key) => {
                      setSelected(Number(key))
                      scrollToTab(Number(key))
                    }}
                    classNames={{
                      base: 'max-w-full',
                      tabList:
                        'bg-[#33385E] backdrop-blur-md p-0 max-w-full overflow-x-auto',
                      tab: `bg-transparent py-[22px] ${tabs.length > 4 ? 'sm:px-4' : 'sm:px-6'}`,
                      cursor: 'bg-[#E7EAFF] overflow-hidden',
                      tabContent:
                        'text-[#B3B3B3] font-regular text-sm group-data-[selected=true]:text-uguPurple relative z-10',
                    }}
                    radius="full"
                    size="lg"
                  >
                    {tabs.map((tab, index) => (
                      <Tab key={index} title={tab.label} />
                    ))}
                  </Tabs>
                </div>
              )}
              {tabs.map((tab, index) =>
                width < DESKTOP_BREAKPOINT || index == selected ? (
                  <motion.div
                    whileInView={{
                      opacity: 1,
                      y: 0,
                    }}
                    initial={{
                      opacity: 0,
                      y: 60,
                    }}
                    transition={{
                      duration: 0.5,
                      ease: 'easeInOut',
                    }}
                    key={index}
                    className={`max-h-full ${index + 1 === tabs.length ? 'lg:mb-0 mb-16' : 'lg:mb-0 mb-28'}`}
                  >
                    <TabContent>
                      <TextContent className="lg:mt-4 lg:max-w-[420px]">
                        <TabTitle className="text-left">{tab.title}</TabTitle>
                        <TabDescription className="text-left">
                          {tab.description}
                        </TabDescription>
                      </TextContent>
                      {/* <motion.div viewport={{ amount: 0.6 }}> */}
                      <VisualizationArea>
                        <Image
                          src={tab.image}
                          alt={tab.alt}
                          width={850}
                          height={450}
                          className="w-full h-auto object-contain rounded-xl"
                        />
                      </VisualizationArea>
                      {/* </motion.div> */}
                    </TabContent>
                  </motion.div>
                ) : (
                  <></>
                )
              )}
            </Header>
          </ContentWrapper>
        </Container>
      </div>
    </div>
  )
}

export default MustExistSection
